import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Tag } from '../../components';
import type { ScheduledEvent } from '../event-details';
import type { CloutikRouter } from './use-cloutik';
import { MOCK_PORTS, mockActivity } from './use-cloutik';
import type { CloutikRouterRaw } from './use-routers';
import { useRouters } from './use-routers';

interface Props {
  scheduledEvent: ScheduledEvent;
}

interface NetworkStat {
  title: string;
  value: string;
  unit?: string;
  info?: string;
}

const Empty = styled.div`
  padding: 40px 4px;
  text-align: center;
  color: rgb(138, 152, 181);
  font-size: 14px;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 34px;
`;

const Ident = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;

  .name {
    color: rgb(240, 242, 245);
    font-weight: 700;
    font-size: 15px;
  }

  .synced {
    color: rgb(107, 122, 150);
    font-weight: 500;
    font-size: 11px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const btnStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 13px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(17, 27, 51, 0.6);
  color: rgba(213, 221, 238, 0.92);
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  text-decoration: none;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: var(--white);
    border-color: rgba(255, 255, 255, 0.2);
    text-decoration: none;
  }
`;

const Btn = styled.button`
  ${btnStyles}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LinkBtn = styled.a`
  ${btnStyles}
`;

const Stats = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.3);
  overflow: hidden;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
  gap: 1px;
  background: rgba(255, 255, 255, 0.06);
`;

const Cell = styled.div`
  background: rgba(8, 14, 30, 0.35);
  padding: 13px 15px;

  .title {
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.5);
    font-weight: 700;
    font-size: 9px;
  }

  .value-row {
    margin-top: 7px;
    display: flex;
    align-items: baseline;
    gap: 5px;
    min-width: 0;
  }

  .value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgb(240, 242, 245);
    font-weight: 800;
    font-size: 19px;
    font-family: 'JetBrains Mono', monospace;
  }

  .unit {
    flex-shrink: 0;
    color: rgba(176, 186, 209, 0.55);
    font-weight: 600;
    font-size: 11px;
  }

  .info {
    margin-top: 4px;
    color: rgba(176, 186, 209, 0.5);
    font-weight: 500;
    font-size: 9px;
  }
`;

const PortStatus = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.3);
  padding: 15px 16px;

  .title {
    margin: 0 0 12px;
    font: 700 14px;
    color: rgb(240, 242, 245);
  }
`;

const PortGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 9px;
`;

const PortCell = styled.div<{ $connected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 11px 12px 11px 15px;
  border-radius: 9px;
  overflow: hidden;
  border: 1px solid
    ${({ $connected }): string => ($connected ? 'rgba(53, 211, 153, 0.22)' : 'rgba(255, 255, 255, 0.07)')};
  background: ${({ $connected }): string => ($connected ? 'rgba(53, 211, 153, 0.07)' : 'transparent')};

  .bar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: rgb(124, 198, 240);
  }

  .row1 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .pid {
    color: rgba(205, 214, 234, 0.82);
    font-weight: 600;
    font-size: 11px;
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $connected }): string =>
      $connected ? 'rgb(53, 211, 153)' : 'rgba(176, 186, 209, 0.4)'};
  }

  .pname {
    margin-top: 4px;
    color: rgb(230, 235, 245);
    font-weight: 600;
    font-size: 12px;
  }

  .status-row {
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
  }

  .status {
    letter-spacing: 0.02em;
    font-weight: 600;
    font-size: 10px;
    color: ${({ $connected }): string =>
      $connected ? 'rgb(127, 230, 184)' : 'rgba(176, 186, 209, 0.5)'};
  }

  .speed {
    color: rgba(176, 186, 209, 0.6);
    font-weight: 500;
    font-size: 10px;
    font-family: 'JetBrains Mono', monospace;
  }
`;

const Activity = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.3);
  padding: 15px 16px;

  .title {
    margin: 0 0 12px;
    font: 700 14px;
    color: rgb(240, 242, 245);
  }
`;

const ActivityScroll = styled.div`
  max-height: 260px;
  overflow-y: auto;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
`;

const Entry = styled.div<{ $good: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 9px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-top: 5px;
    flex-shrink: 0;
    background: ${({ $good }): string =>
      $good ? 'rgb(53, 211, 153)' : 'rgba(176, 186, 209, 0.5)'};
  }

  .text {
    flex: 1 1 0%;
    min-width: 0;
  }

  .msg {
    color: rgba(213, 221, 238, 0.9);
    overflow-wrap: anywhere;
    font-weight: 600;
    font-size: 12px;
  }

  .time {
    flex-shrink: 0;
    white-space: nowrap;
    color: rgba(176, 186, 209, 0.5);
    font-weight: 500;
    font-size: 11px;
  }
`;

const MIN_PER_HOUR = 60;
const MIN_PER_DAY = 60 * 24;

const syncedText = (minsAgo: number): string => {
  if (minsAgo < 1) {
    return 'synced just now';
  }

  if (minsAgo < MIN_PER_HOUR) {
    return `synced ${minsAgo} min ago`;
  }

  if (minsAgo < MIN_PER_DAY) {
    return `synced ${Math.floor(minsAgo / MIN_PER_HOUR)} hr ago`;
  }

  return `synced ${Math.floor(minsAgo / MIN_PER_DAY)}d ago`;
};

const SEC_PER_MIN = 60;
const SEC_PER_HOUR = 60 * 60;
const SEC_PER_DAY = 60 * 60 * 24;

// Uptime as "Xd Yh" (e.g. "4d 0h"); under an hour it falls back to minutes ("45m").
const formatUptime = (sec?: number): string => {
  if (sec === undefined || Number.isNaN(sec) || sec <= 0) {
    return '—';
  }

  if (sec < SEC_PER_HOUR) {
    return `${Math.floor(sec / SEC_PER_MIN)}m`;
  }

  const days = Math.floor(sec / SEC_PER_DAY);
  const hours = Math.floor((sec % SEC_PER_DAY) / SEC_PER_HOUR);

  return `${days}d ${hours}h`;
};

// Relative "synced X min ago" label that refreshes itself every minute.
const SyncedLabel = ({ at }: { at?: string | null }): ReactElement | null => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000);

    return (): void => {
      clearInterval(id);
    };
  }, []);

  const value = at ?? '';

  if (value.length === 0) {
    return null;
  }

  const minsAgo = Math.max(0, Math.floor(-DateTime.fromISO(value).diffNow('minutes').minutes));

  return <span className='synced'>{syncedText(minsAgo)}</span>;
};

// Relative time for activity rows: "just now" / "X min ago" / "X hr ago" / "X d ago".
const timeAgo = (at: string): string => {
  if (at.length === 0) {
    return '';
  }

  const mins = Math.max(0, Math.floor(-DateTime.fromISO(at).diffNow('minutes').minutes));

  if (mins < 1) {
    return 'just now';
  }

  if (mins < MIN_PER_HOUR) {
    return `${mins} min ago`;
  }

  if (mins < MIN_PER_DAY) {
    return `${Math.floor(mins / MIN_PER_HOUR)} hr ago`;
  }

  return `${Math.floor(mins / MIN_PER_DAY)} d ago`;
};

// Leading "#<cid>" token from a router name (e.g. "#1221.2 - ..." → "1221.2", "#1940 - ..." → "1940").
const parseRouterCid = (name?: string | null): string => {
  const match = /^#(?<cid>[\d.]+)/.exec(name ?? '');

  return match?.groups?.cid ?? '';
};

/*
 * Pick the router for this location: match "#<customerId>.<secondary>". For the first
 * location, a single-location customer's name omits the ".1", so fall back to just
 * "#<customerId>".
 */
const findRouter = (
  routers: CloutikRouterRaw[],
  customerId: number,
  secondary?: number | null
): CloutikRouterRaw | undefined => {
  const target = `${customerId}.${secondary ?? ''}`;
  const exact = routers.find((r) => parseRouterCid(r.name) === target);

  if (exact) {
    return exact;
  }

  if (secondary === 1) {
    return routers.find((r) => parseRouterCid(r.name) === String(customerId));
  }

  return undefined;
};

export const NetworkTab = ({ scheduledEvent }: Props): ReactElement => {
  const { location } = scheduledEvent;
  const customerId = Number(location?.user?.id ?? Number.NaN);

  const { routers, loading, error, refetch } = useRouters(customerId);
  const matched = findRouter(routers, customerId, location?.secondary_id);

  if (loading && routers.length === 0) {
    return <Empty>Checking router…</Empty>;
  }

  if (error && routers.length === 0) {
    return <Empty>Couldn’t load network status.</Empty>;
  }

  if (!matched) {
    return <Empty>No router linked.</Empty>;
  }

  // Header + stats from the real router; ports and recent activity stay mock for now.
  const router: CloutikRouter = {
    model: matched.model ?? undefined,
    online: matched.status === 'online',
    lastConAt: matched.last_con,
    uptimeSec: Number(matched.uptime),
    wanIp: matched.ip ?? undefined,
    serial: matched.serial ?? undefined,
    cloutikUrl: `https://app.cloutik.us/device/devices/${matched.id}`,
    ports: MOCK_PORTS,
    activity: mockActivity()
  };

  const online = router.online ?? false;

  const stats: NetworkStat[] = [
    { title: 'Uptime', value: formatUptime(router.uptimeSec) },
    { title: 'WAN IP', value: router.wanIp ?? '—' },
    { title: 'Serial', value: router.serial ?? '—' }
  ];

  return (
    <Root>
      <Header>
        <Ident>
          <span className='name'>{router.model ?? 'Router'}</span>

          {online ? (
            <Tag className='cc-green' icon='fas fa-circle' iconSize={7}>
              ONLINE
            </Tag>
          ) : (
            <Tag className='cc-amber' icon='fas fa-circle' iconSize={7}>
              No recent data
            </Tag>
          )}

          <SyncedLabel at={router.lastConAt} />
        </Ident>

        <Actions>
          <Btn type='button' onClick={refetch} disabled={loading}>
            <i className='fas fa-sync-alt' /> Refresh
          </Btn>

          {router.cloutikUrl !== undefined && router.cloutikUrl.length > 0 ? (
            <LinkBtn href={router.cloutikUrl} target='_blank' rel='noreferrer'>
              Open in Cloutik <i className='far fa-external-link' />
            </LinkBtn>
          ) : (
            <Btn type='button' disabled>
              Open in Cloutik <i className='far fa-external-link' />
            </Btn>
          )}
        </Actions>
      </Header>

      <Stats>
        <StatGrid>
          {stats.map((stat) => (
            <Cell key={stat.title}>
              <div className='title'>{stat.title}</div>
              <div className='value-row'>
                <span className='value'>{stat.value}</span>
                {stat.unit !== undefined && <span className='unit'>{stat.unit}</span>}
              </div>
              {stat.info !== undefined && <div className='info'>{stat.info}</div>}
            </Cell>
          ))}
        </StatGrid>
      </Stats>

      {router.ports !== undefined && router.ports.length > 0 && (
        <PortStatus>
          <div className='title'>Port Status</div>
          <PortGrid>
            {router.ports.map((port) => {
              const connected = port.state === 'up';
              const name = port.label.length > 0 ? port.label : '—';

              return (
                <PortCell key={port.id} $connected={connected}>
                  {connected && <span className='bar' />}

                  <div className='row1'>
                    <span className='pid'>{port.id}</span>
                    <span className='dot' />
                  </div>

                  <div className='pname'>{name}</div>

                  <div className='status-row'>
                    <span className='status'>{connected ? 'Connected' : 'Not connected'}</span>
                    {connected && <span className='speed'>{port.speed}</span>}
                  </div>
                </PortCell>
              );
            })}
          </PortGrid>
        </PortStatus>
      )}

      {router.activity !== undefined && router.activity.length > 0 && (
        <Activity>
          <div className='title'>Recent activity</div>
          <ActivityScroll className='cc-scroll'>
            <ActivityList>
              {router.activity.map((item) => (
                <Entry key={`${item.at ?? ''}-${item.message}`} $good={item.good ?? false}>
                  <span className='dot' />
                  <div className='text'>
                    <span className='msg'>{item.message}</span>
                  </div>
                  <span className='time'>{timeAgo(item.at ?? '')}</span>
                </Entry>
              ))}
            </ActivityList>
          </ActivityScroll>
        </Activity>
      )}
    </Root>
  );
};
